import React, { useState, useRef, useEffect } from 'react';

const CustomBookingTypeDropdown = ({ options, value, name, onChange, required = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const selected = options.find(option => option.value === value);
    setSelectedOption(selected || null);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOptionClick = (option) => {
    if (!option.enabled) return;
    
    setSelectedOption(option);
    setIsOpen(false);
    
    const syntheticEvent = {
      target: {
        name: name,
        value: option.value
      }
    };
    onChange(syntheticEvent);
  };

  const handleKeyDown = (event, option) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOptionClick(option);
    }
  };

  return (
    <div className="custom-dropdown" ref={dropdownRef} required>
      <div 
        className={`form-control wide ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
            }
        }}
        tabIndex={0}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-required={required}
        data-required={required}
        style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start'
        }}
        required
        >
        {selectedOption ? selectedOption.label : 'Select'}
      </div>
      
      {isOpen && (
        <div className="dropdown-options" role="listbox">          
          {options.map((option) => (
            <div
              key={option.value}
              className={`dropdown-option ${!option.enabled ? 'disabled' : ''} ${selectedOption?.value === option.value ? 'selected' : ''}`}
              onClick={() => handleOptionClick(option)}
              onKeyDown={(e) => handleKeyDown(e, option)}
              tabIndex={option.enabled ? 0 : -1}
              role="option"
              aria-selected={selectedOption?.value === option.value}
              aria-disabled={!option.enabled}
            >
              <div className="option-label">{option.label}</div>
              {option.tooltip && (
                <div className="option-tooltip">{option.tooltip}</div>
              )}
            </div>
          ))}
        </div>
      )}

      <style jsx>{`
        .custom-dropdown {
          position: relative;
          width: 100%;
        }

        .dropdown-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
        }

        .dropdown-header.open {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }

        .selected-text {
          flex: 1;
        }


        .form-control[data-required="true"]:invalid,

        .dropdown-options {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background-color: white;
          border: 1px solid;
          border-radius: 0 0 4px 4px;
          max-height: 200px;
          overflow-y: auto;
          z-index: 1000;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          color: #6e6e6e
        }

        .dropdown-option {
          padding: 8px 12px;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .dropdown-option:last-child {
          border-bottom: none;
        }

        .dropdown-option:hover:not(.disabled) {
          background-color: #f8f9fa;
        }

        .dropdown-option:focus {
          outline: none;
          background-color: #e9ecef;
        }

        .dropdown-option.selected {
          background-color: #e3f2fd;
        }

        .dropdown-option.disabled {
          cursor: not-allowed;
          opacity: 0.6;
          background-color: #f5f5f5;
        }

        .option-tooltip {
          font-size: 0.85em;
          margin-top: 2px;
          line-height: 1.3;
        }

        /* Scrollbar styling */
        .dropdown-options::-webkit-scrollbar {
          width: 6px;
        }

        .dropdown-options::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        .dropdown-options::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        .dropdown-options::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </div>
  );
};

export default CustomBookingTypeDropdown;